import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import RestoreRoundedIcon from "@mui/icons-material/RestoreRounded";
import { buildingsApi } from "../../api/buildings";

const RETENTION_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

function getErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) {
    const serials = Array.isArray(detail.serial_numbers)
      ? detail.serial_numbers.slice(0, 5).join(", ")
      : "";
    return serials ? `${detail.message} Совпадения: ${serials}.` : detail.message;
  }
  return "Не удалось выполнить операцию. Попробуйте ещё раз.";
}

function getDeviceCount(building) {
  return building.device_counts?.total ?? building.device_count ?? 0;
}

function getRetention(building) {
  const now = Date.now();
  const archivedAt = building.archived_at ? new Date(building.archived_at).getTime() : now;
  const purgeAt = building.purge_after
    ? new Date(building.purge_after).getTime()
    : archivedAt + RETENTION_DAYS * DAY_MS;
  const remainingMs = Math.max(0, purgeAt - now);
  const remainingDays = Math.max(0, Math.ceil(remainingMs / DAY_MS));
  const totalMs = Math.max(DAY_MS, purgeAt - archivedAt);
  const remainingPercent = Math.min(100, Math.max(0, (remainingMs / totalMs) * 100));
  return { purgeAt, remainingDays, remainingPercent };
}

export default function BuildingArchiveDialog({
  open,
  onClose,
  complexId,
  onRestored,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!complexId) return;
    setLoading(true);
    setError("");
    try {
      setItems(await buildingsApi.getArchivedBuildings(String(complexId)));
    } catch (loadError) {
      setError(getErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [complexId]);

  useEffect(() => {
    if (open) load();
  }, [load, open]);

  const totalDevices = useMemo(
    () => items.reduce((total, item) => total + Number(getDeviceCount(item) || 0), 0),
    [items]
  );

  const handleRestore = async (building) => {
    setRestoringId(building.id);
    setError("");
    try {
      await buildingsApi.restoreBuilding(String(building.id));
      setItems((current) => current.filter((item) => item.id !== building.id));
      await onRestored?.(building);
    } catch (restoreError) {
      setError(getErrorMessage(restoreError));
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1.25 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              color: "warning.dark",
              bgcolor: "rgba(237, 108, 2, 0.10)",
            }}
          >
            <DeleteSweepOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={800}>Корзина объектов</Typography>
            <Typography variant="body2" color="text.secondary">
              Объекты хранятся 7 дней. Сбор показаний остановлен.
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "background.default" }}>
        <Stack spacing={1.5}>
          {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}

          {!loading && items.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip icon={<Inventory2OutlinedIcon />} label={`Объектов: ${items.length}`} />
              <Chip label={`Приборов: ${new Intl.NumberFormat("ru-RU").format(totalDevices)}`} />
            </Stack>
          )}

          {loading && (
            <Box sx={{ py: 7, display: "grid", placeItems: "center" }}>
              <CircularProgress size={30} />
            </Box>
          )}

          {!loading && items.length === 0 && !error && (
            <Paper variant="outlined" sx={{ p: 4, borderRadius: 2.5, textAlign: "center" }}>
              <Inventory2OutlinedIcon color="disabled" sx={{ fontSize: 42, mb: 1 }} />
              <Typography fontWeight={750}>Корзина пуста</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Удалённые объекты будут отображаться здесь в течение 7 дней.
              </Typography>
            </Paper>
          )}

          {!loading && items.map((building) => {
            const retention = getRetention(building);
            const deviceCount = getDeviceCount(building);
            const restoring = restoringId === building.id;
            return (
              <Paper
                key={building.id}
                variant="outlined"
                sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, overflow: "hidden" }}
              >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      flex: "0 0 auto",
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      color: "primary.main",
                      bgcolor: "action.hover",
                    }}
                  >
                    <HomeWorkOutlinedIcon />
                  </Box>
                  <Stack spacing={0.7} sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography fontWeight={800}>{building.name || "Без названия"}</Typography>
                      <Chip size="small" color="warning" variant="outlined" label="В корзине" />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {building.address || "Адрес не указан"}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Typography variant="caption" color="text.secondary">
                        Приборов: {new Intl.NumberFormat("ru-RU").format(deviceCount)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Удаление: {new Date(retention.purgeAt).toLocaleString("ru-RU")}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LinearProgress
                        variant="determinate"
                        value={retention.remainingPercent}
                        color={retention.remainingDays <= 2 ? "error" : "warning"}
                        sx={{ flex: 1, height: 6, borderRadius: 3 }}
                      />
                      <Typography variant="caption" fontWeight={700} sx={{ whiteSpace: "nowrap" }}>
                        {retention.remainingDays > 0
                          ? `${retention.remainingDays} дн.`
                          : "Срок истёк"}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Button
                    variant="outlined"
                    startIcon={restoring ? <CircularProgress size={16} /> : <RestoreRoundedIcon />}
                    disabled={Boolean(restoringId)}
                    onClick={() => handleRestore(building)}
                    sx={{ alignSelf: { xs: "stretch", sm: "center" }, whiteSpace: "nowrap" }}
                  >
                    Восстановить
                  </Button>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
}
